// * https://react-icons.github.io/react-icons/

import { AiOutlineDelete } from 'react-icons/ai';
import { BsArrowsExpand, BsFillPersonPlusFill } from 'react-icons/bs';
import { FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import { FaChevronLeft, FaPlus } from 'react-icons/fa6';
import { IoClose, IoEye, IoEyeOff, IoCheckboxOutline } from 'react-icons/io5';
import { LuArrowUpToLine, LuTimer } from 'react-icons/lu';
import { PiLinkSimpleBold } from 'react-icons/pi';
import { RiErrorWarningFill } from 'react-icons/ri';
import { TbBuildingPlus } from 'react-icons/tb';
import { TiWarning } from 'react-icons/ti';

import { InlineStyles } from '@web/types';

import { Tooltip } from './Tooltip';

type IconProps = {
  size?: number;
  styles?: InlineStyles;
};

export function InfoIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <FaInfoCircle
      size={size}
      aria-label="Info Icon"
      style={{ color: 'var(--nn-accent-blue)', ...styles }}
    />
  );
}

export function CheckIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <FaCheckCircle
      size={size}
      aria-label="Check Icon"
      style={{ color: 'var(--nn-accent-green)', ...styles }}
    />
  );
}

export function WarningIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <TiWarning
      size={size}
      aria-label="Warning Icon"
      style={{ color: 'var(--nn-accent-orange)', ...styles }}
    />
  );
}

export function ErrorIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <RiErrorWarningFill
      size={size}
      aria-label="Error Icon"
      style={{ color: 'var(--nn-accent-red)', ...styles }}
    />
  );
}

export function EyeIcon({
  open,
  size = 24,
  styles = {},
}: IconProps & { open?: boolean }) {
  return open ? (
    <IoEye
      size={size}
      aria-label="Eye Icon"
      style={{ color: 'var(--nn-color-primary)', ...styles }}
    />
  ) : (
    <IoEyeOff
      size={size}
      aria-label="Eye Icon"
      style={{ color: 'var(--nn-color-primary)', ...styles }}
    />
  );
}

export function CheckboxIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <IoCheckboxOutline
      size={size}
      aria-label="Checkbox Icon"
      style={{ color: 'var(--nn-color-text)', ...styles }}
    />
  );
}

export function ExpandIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <BsArrowsExpand
      size={size}
      aria-label="Expand Icon"
      style={{ color: 'var(--nn-color-interactive)', ...styles }}
    />
  );
}

export function AddCompanyIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <TbBuildingPlus
      size={size}
      aria-label="Add Company Icon"
      style={{ color: 'var(--nn-color-interactive)', ...styles }}
    />
  );
}

export function AddPersonIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <BsFillPersonPlusFill
      size={size}
      aria-label="Add Person Icon"
      style={{ color: 'var(--nn-color-interactive)', ...styles }}
    />
  );
}

export function ImportIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <LuArrowUpToLine
      size={size}
      aria-label="Arrow Up To Line Icon"
      style={{ color: 'var(--nn-color-interactive)', ...styles }}
    />
  );
}

export function ChevronIcon({
  size = 16,
  direction = 'left',
  styles = {},
}: IconProps & { direction?: 'left' | 'right' | 'up' | 'down' }) {
  return (
    <FaChevronLeft
      size={size}
      aria-label="Chevron Icon"
      style={{
        color: 'var(--nn-color-interactive)',
        transform:
          direction === 'left'
            ? 'rotate(0deg)'
            : direction === 'right'
              ? 'rotate(-180deg)'
              : direction === 'up'
                ? 'rotate(90deg)'
                : 'rotate(-90deg)',
        ...styles,
      }}
    />
  );
}

export function PlusIcon({ size = 14, styles = {} }: IconProps) {
  return (
    <FaPlus
      size={size}
      aria-label="Plus Icon"
      style={{ color: 'var(--nn-color-interactive)', ...styles }}
    />
  );
}

// * If you need a 'copy link' behavior, use the 'CopyLinkIcon' component instead.
export function LinkIcon({
  size = 18,
  showTooltip = true,
  tooltipTitle = 'Copy link',
  styles = {},
}: IconProps & { showTooltip?: boolean; tooltipTitle?: string }) {
  return (
    <Tooltip title={showTooltip ? tooltipTitle : undefined}>
      <PiLinkSimpleBold
        size={size}
        aria-label={tooltipTitle}
        style={{
          color: 'var(--nn-color-interactive)',
          cursor: 'pointer',
          ...styles,
        }}
      />
    </Tooltip>
  );
}

export function DeleteIcon({ size = 24, styles = {} }: IconProps) {
  return (
    <AiOutlineDelete
      size={size}
      aria-label="Delete Icon"
      style={{ color: 'var(--nn-color-text)', ...styles }}
    />
  );
}

export function CloseIcon({ size = 18, styles = {} }: IconProps) {
  return (
    <IoClose
      size={size}
      aria-label="Close Icon"
      style={{ color: 'var(--nn-color-interactive)', ...styles }}
    />
  );
}

export function TimerIcon({ size = 18, styles = {} }: IconProps) {
  return (
    <LuTimer
      size={size}
      aria-label="Timer Icon"
      style={{ color: 'var(--nn-color-interactive)', ...styles }}
    />
  );
}
